import React, { useEffect, useCallback, useRef, useState } from "react";
import {
  FiPlus,
  FiFolderPlus,
  FiArrowUp,
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiEdit,
  FiTrash2,
  FiCopy,
  FiClipboard,
  FiXCircle,
  FiCheckCircle,
  FiX,
  FiSave,
  FiRotateCcw,
  FiFileText,
  FiRefreshCw,
} from "react-icons/fi";
import {
  DEFAULT_SHORTCUTS,
  isMacOS,
  WINDOWS_DEFAULT_SHORTCUTS,
} from "../common/common";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setShortCuts } from "../redux/shortcut/shortcutSlice";
import { update_shortcuts_thunk } from "../redux/shortcut/shortcutThunk";

const ICONS_BY_ID = {
  "sidebar-add-todo": <FiPlus />,
  "sidebar-add-folder": <FiFolderPlus />,
  "sidebar-nav-up": <FiArrowUp />,
  "sidebar-nav-down": <FiArrowDown />,
  "sidebar-input-close": <FiXCircle />,
  "sidebar-nav-left": <FiArrowLeft />, // Icon for navigating to folder section
  "sidebar-nav-right": <FiArrowRight />, // Icon for navigating to file section
  "sidebar-open-file": <FiFileText />, // Icon for opening a specific file
  "sidebar-sync": <FiRefreshCw />, // Icon for opening a specific file
  "tab-prev": <FiArrowLeft />,
  "tab-next": <FiArrowRight />,
  "tab-specific": <FiCheckCircle />,
  "tab-close": <FiX />,
  "todo-focus": <FiEdit />,
  "todo-delete": <FiTrash2 />,
  "todo-copy": <FiCopy />,
  "todo-paste": <FiClipboard />,
  "todo-undo": <FiXCircle />,
};

// Default keyboard shortcuts

export default function ShortcutHelperModal({ isOpen, onClose }) {
  const token = localStorage.getItem("todoToken");
  const modalRef = useRef(null);
  const editingInputRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  // State for managing shortcuts
  const shortcuts = useSelector((state) => state.shortcut?.shortcuts);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tempKeyCombination, setTempKeyCombination] = useState("");

  // Handle scroll
  const handleScroll = (e) => {
    setIsScrolled(e.target.scrollTop > 0);
  };

  // Handle key down events
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        if (isEditing) {
          setEditingShortcut(null);
          setIsEditing(false);
          setTempKeyCombination("");
        } else {
          onClose();
        }
      }
    },
    [onClose, isEditing]
  );

  // Set up event listeners
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle mouse movement for gradient effect
  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     if (modalRef.current) {
  //       const rect = modalRef.current.getBoundingClientRect();
  //       setMousePosition({
  //         x: e.clientX - rect.left,
  //         y: e.clientY - rect.top,
  //       });
  //     }
  //   };

  //   if (isOpen) {
  //     window.addEventListener("mousemove", handleMouseMove);
  //   }

  //   return () => {
  //     window.removeEventListener("mousemove", handleMouseMove);
  //   };
  // }, [isOpen]);

  // Handle clicking outside the modal
  const handleClickOutside = useCallback(
    (event) => {
      if (
        isEditing &&
        editingInputRef.current &&
        !editingInputRef.current.contains(event.target) // Check if the click is outside the editing input
      ) {
        setEditingShortcut(null);
        setIsEditing(false);
        setTempKeyCombination("");
      }
    },
    [isEditing]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Start editing a shortcut
  const startEditingShortcut = (shortcut) => {
    setEditingShortcut(shortcut);
    setIsEditing(true);
    setTempKeyCombination("");
  };

  // Handle shortcut key capture
  const handleShortcutKeyCapture = (event) => {
    if (!isEditing || !editingShortcut) return;

    // Handle Enter key to confirm the change
    if (event.key === "Enter") {
      if (tempKeyCombination) {
        const updatedShortcuts = { ...shortcuts };
        Object.keys(updatedShortcuts).forEach((section) => {
          const sectionShortcuts = updatedShortcuts[section].map((s) =>
            s.id === editingShortcut.id ? { ...s, key: tempKeyCombination } : s
          );
          updatedShortcuts[section] = sectionShortcuts;
        });
        dispatch(setShortCuts(updatedShortcuts));
        dispatch(
          update_shortcuts_thunk({ data: updatedShortcuts, token: token })
        );
        setEditingShortcut(null);
        setIsEditing(false);
        setTempKeyCombination("");
      }
      return;
    }

    // Prevent default behavior to stop special character generation
    event.preventDefault();
    event.stopPropagation();

    // Skip modifier keys when pressed alone
    if (["Control", "Alt", "Shift", "Meta", "Enter"].includes(event.key)) {
      return;
    }

    // Construct the new key combination
    const modifiers = [];
    if (event.ctrlKey) modifiers.push("Ctrl");
    if (event.altKey) {
      let isMac = isMacOS();
      if (isMac) {
        modifiers.push("Option");
      } else {
        modifiers.push("Alt");
      }
    }
    if (event.shiftKey) modifiers.push("Shift");
    if (event.metaKey) modifiers.push("Cmd");

    // Handle special keys
    let key = event.key;
    if (key === " ") key = "Space";

    // Ensure original character is used, not the special/alternate character
    if (event.altKey) {
      // Use the base key without converting to uppercase
      key = event.code.replace("Key", "");
    } else if (key.length === 1) {
      key = key.toUpperCase();
    }

    const newKey = [...modifiers, key].join("+");
    setTempKeyCombination(newKey);
  };

  // Reset shortcuts to default
  const resetShortcuts = () => {
    let osShortcuts = isMacOS ? DEFAULT_SHORTCUTS : WINDOWS_DEFAULT_SHORTCUTS;
    dispatch(setShortCuts(osShortcuts));
    let data = {
      shortcuts: DEFAULT_SHORTCUTS,
      windowsShortcuts: WINDOWS_DEFAULT_SHORTCUTS,
    };
    dispatch(update_shortcuts_thunk({ data: data, token: token }));
  };
  return (
    isOpen && (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onKeyDown={handleShortcutKeyCapture}
        tabIndex={-1}
      >
        <div
          ref={modalRef}
          className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-xl shadow-2xl p-6 w-full max-w-lg border border-gray-700/50 max-h-[90vh] flex flex-col"
        >
          <div
            className="absolute inset-0 pointer-events-none rounded-xl"
            // style={{
            //   background: `radial-gradient(circle 200px at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.1), transparent 100%)`,
            // }}
          />
          <div
            className={`flex justify-between items-center border-b border-gray-700/50 pb-4 relative ${
              isScrolled
                ? "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-6 after:bg-gradient-to-b after:from-gray-900/20 after:to-transparent after:-mb-6 after:pointer-events-none"
                : ""
            }`}
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Keyboard Shortcuts
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={resetShortcuts}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/50"
                aria-label="Reset shortcuts"
                title="Reset to default shortcuts"
              >
                <FiRotateCcw className="w-5 h-5" />
              </button>
              {/* <button
                onClick={saveShortcuts}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/50"
                aria-label="Save shortcuts"
                title="Save custom shortcuts"
              >
                <FiSave className="w-5 h-5" />
              </button> */}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/50"
                aria-label="Close modal"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto fancy-scrollbar relative scroll-smooth"
          >
            {Object.entries(shortcuts).map(
              ([section, sectionShortcuts], sectionIndex) => (
                <div
                  key={section}
                  className={`mt-6 ${
                    sectionIndex > 0 ? "border-t border-gray-700/30 pt-6" : ""
                  }`}
                >
                  <h3 className="text-lg font-semibold text-blue-400 mb-4 px-1">
                    {section}
                  </h3>
                  <ul className="space-y-3 px-1">
                    {sectionShortcuts.map((shortcut) => (
                      <li
                        key={shortcut.id}
                        className="flex justify-between items-center bg-gray-800/50 p-4 rounded-lg border border-gray-700/30 hover:border-blue-500/30 hover:bg-gray-700/50 transition-all duration-200 group"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-blue-400 bg-blue-400/10 p-2 rounded-lg group-hover:bg-blue-400/20 transition-colors duration-200">
                            {ICONS_BY_ID[shortcut.id]}
                          </span>
                          <span
                            ref={
                              editingShortcut?.id === shortcut.id
                                ? editingInputRef
                                : null
                            }
                            className={`font-mono text-sm text-gray-300 bg-gray-700/50 px-3 py-1 rounded-md group-hover:bg-gray-600/50 transition-colors duration-200 ${
                              editingShortcut?.id === shortcut.id
                                ? "border-2 border-blue-500"
                                : ""
                            }`}
                          >
                            {editingShortcut?.id === shortcut.id
                              ? tempKeyCombination || "Press keys..."
                              : shortcut.key}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-200 mr-2">
                            {shortcut.action}
                          </span>
                          {shortcut.id !== "sidebar-open-file" &&
                            shortcut.id !== "tab-specific" &&
                            shortcut.id !== "todo-undo" &&
                            shortcut.id !== "todo-delete" && (
                              <div
                                onClick={() => startEditingShortcut(shortcut)}
                                className="text-gray-400 hover:text-blue-400 transition-colors duration-200 cursor-pointer"
                                aria-label="Edit shortcut"
                              >
                                <FiEdit className="w-4 h-4" />
                              </div>
                            )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}
          </div>
          <div className="mt-6 text-center text-sm text-gray-400 relative before:absolute before:top-0 before:left-0 before:right-0 before:h-6 before:bg-gradient-to-t before:from-gray-900/20 before:to-transparent before:-mt-6 before:pointer-events-none">
            {isEditing ? (
              <>
                Press{" "}
                <kbd className="px-2 py-1.5 text-xs font-semibold bg-gray-700/50 text-gray-100 border border-gray-600/50 rounded-lg shadow-inner">
                  Enter
                </kbd>{" "}
                to save or click outside to cancel
              </>
            ) : (
              <>
                Press{" "}
                <kbd className="px-2 py-1.5 text-xs font-semibold bg-gray-700/50 text-gray">
                  Escape
                </kbd>{" "}
                to close
              </>
            )}
          </div>
        </div>
      </div>
    )
  );
}
